import { Link } from "react-router-dom";
import { ArrowRight, Compass, PenTool, TrendingUp, MessageCircle } from "lucide-react";
import SEO from "../components/SEO";
import { ORG_ID, founderSchema } from "../data/schema";
import { Reveal, Chapter, MaskedLine } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { waLink, CONTEXT_MESSAGES, WA_NUMBER_DISPLAY } from "../lib/whatsapp";
import { track } from "../lib/analytics";

const VALUES = [
  {
    icon: Compass,
    h: "Strategy before pixels",
    p: "Every project starts with who your customer is and what makes them enquire. Design decisions come after that answer, never before.",
  },
  {
    icon: PenTool,
    h: "Craft that earns trust",
    p: "Premium typography, real whitespace and fast load times aren't decoration — they're how a visitor decides you're credible in the first five seconds.",
  },
  {
    icon: TrendingUp,
    h: "Growth is the deliverable",
    p: "A website that never brings an enquiry has failed, no matter how good it looks. Every build is measured against one thing: does it bring business?",
  },
];

export default function About() {
  return (
    <main className="pt-36 lg:pt-44">
      <SEO
        title="About Grotech Web — Affordable Website Development, AI Websites & Chatbots"
        description="Grotech Web builds premium, SEO-friendly business websites at genuinely affordable cost — 15+ years of website building experience, now with AI websites and AI chatbots for lead generation."
        path="/about"
        schema={[{ "@type": "AboutPage", "@id": "https://grotechweb.com/about/#aboutpage", url: "https://grotechweb.com/about/", name: "About Grotech Web", mainEntity: { "@id": ORG_ID } }, founderSchema]}
      />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="about-hero">
        <div className="max-w-4xl">
          <div>
            <Chapter index="—" label="About Grotech Web" />
            <h1 className="mt-8 font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
              <MaskedLine>Affordable Websites.</MaskedLine>
              <MaskedLine delay={0.15}>
                <span className="text-ember">Founder-Led Craft.</span>
              </MaskedLine>
            </h1>
            <Reveal hero delay={0.3}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/60">
                Grotech Web is a website development studio founded by Somil Grover, who has more than
                15 years of experience building websites. It builds premium, SEO-friendly and genuinely
                affordable websites for businesses in India, the USA and the UAE.
                Today that includes AI-powered websites and AI chatbots that answer your customers
                and capture leads around the clock.
              </p>
            </Reveal>
            <Reveal hero delay={0.4}>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3" data-testid="about-stats">
                {["Founder with 15+ Years in Web", "Affordable Development Cost", "AI Websites & Chatbots"].map((s) => (
                  <span key={s} className="flex items-center gap-2 text-sm text-white/55">
                    <span className="h-1.5 w-1.5 rounded-full bg-ember" /> {s}
                  </span>
                ))}
              </div>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
                New Delhi, India · Working Across India
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Chapter index="01" label="The Belief" />
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <blockquote className="border-l-2 border-ember pl-6 font-display text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
                "A website should do more than exist online. It should clearly explain your business,
                build trust and make it easier for potential customers to contact you."
              </blockquote>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
                — Somil Grover, Founder
              </p>
              <div className="mt-8 space-y-5 text-base leading-relaxed text-white/60">
                <p>
                  Grotech Web has been building websites for over 15 years — through every era of the
                  internet, from simple static pages to today's AI-powered experiences. One thing has
                  never changed: a website only matters if it brings the business enquiries.
                </p>
                <p>
                  We kept seeing the same pattern: good businesses with websites that undersold them
                  completely, built by agencies charging far more than most businesses could justify.
                  Grotech Web exists to close that gap — premium, fast, SEO-friendly websites at a
                  cost a real business can actually afford.
                </p>
                <p>
                  And for businesses ready to go further, we build AI into the website itself:
                  AI-powered websites that adapt and optimise, and AI chatbots that greet visitors,
                  answer questions and capture leads even at midnight — so no enquiry is ever lost
                  to timing.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Chapter index="02" label="The Entity" />
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                What is Grotech Web?
              </h2>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-white/60">
                <p>
                  Grotech Web is a website development and lead-generation focused web design service
                  helping businesses create professional, fast and conversion-focused websites. Grotech
                  Web works with growing businesses, service companies, B2B companies, startups and
                  professionals across India (including Mumbai, Delhi, Noida and Gurgaon), the USA and the UAE.
                </p>
                <p>
                  Led by a founder with 15+ years of website building experience, the studio builds business websites,
                  corporate websites, B2B lead generation websites, SEO-friendly websites, landing
                  pages, AI-powered websites and AI chatbots — at a genuinely affordable cost, with an
                  optional monthly growth plan for businesses that want compounding results.
                </p>
              </div>
              <div className="mt-8 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-3">
                {[
                  ["What", "SEO & lead generation website development"],
                  ["Who", "Growing businesses, B2B companies, startups, professionals"],
                  ["Where", "India (Mumbai · Delhi · Noida · Gurgaon) · USA · UAE (Dubai)"],
                ].map(([k, v]) => (
                  <div key={k} className="bg-obsidian p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">{k}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/70">{v}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-bone py-20 text-obsidian lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Chapter index="03" label="How We Work" dark />
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.h} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-obsidian/10 bg-white p-8">
                  <v.icon className="h-6 w-6 text-ember" />
                  <h2 className="mt-5 font-display text-xl font-bold tracking-tight">{v.h}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-obsidian/60">{v.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Chapter index="04" label="Expertise" />
          </div>
          <div className="lg:col-span-8">
            <div className="grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2">
              {[
                "Affordable business website development",
                "AI-powered website development",
                "AI chatbot & WhatsApp automation",
                "B2B lead-generation website strategy",
                "SEO-first website architecture",
                "Local SEO & location landing pages",
                "Conversion-focused copy & CTAs",
                "Blog & organic growth systems",
                "Website redesign & migration",
                "Performance & speed engineering",
              ].map((e) => (
                <div key={e} className="bg-obsidian p-5 text-sm text-white/70">
                  {e}
                </div>
              ))}
            </div>
            <Reveal>
              <div className="mt-12 rounded-3xl bg-ember p-8 sm:p-10">
                <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  Let's talk about your business.
                </h2>
                <p className="mt-3 max-w-md text-sm text-white/85">
                  A short conversation is enough to know whether a new website — or a smarter one —
                  would pay for itself.
                </p>
                <div className="mt-7 flex flex-wrap gap-4">
                  <button
                    data-testid="about-cta-btn"
                    onClick={() => { track("cta_click", { location: "about" }); openLeadPopup(); }}
                    className="group flex items-center gap-2 rounded-full bg-white px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-obsidian transition-transform hover:scale-105"
                  >
                    Get a Free Website Plan <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <a
                    href={waLink(CONTEXT_MESSAGES.general)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="about-whatsapp-btn"
                    onClick={() => track("whatsapp_click", { location: "about" })}
                    className="flex items-center gap-2 rounded-full border border-white/40 px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white/10"
                  >
                    <MessageCircle className="h-4 w-4" /> {WA_NUMBER_DISPLAY}
                  </a>
                </div>
                <p className="mt-5 text-xs text-white/80">
                  <Link to="/reviews/" className="underline underline-offset-2 hover:text-white">
                    Explore client feedback on our work
                  </Link>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
